import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import Q8Breadcrumbs from "../components/Q8Breadcrumbs";
import Q8Button from "../components/Q8Button";
import Q8SnackBar from "../components/Q8SnackBar";
import {
  Add as AddIcon,
  FileDownload as ExportIcon,
  FilterList as FilterIcon,
  Delete as DeleteIcon,
  InfoOutlined,
} from "@mui/icons-material";
import "tabulator-tables/dist/css/tabulator.min.css";
import "../styles/tabulator.css";
import * as XLSX from "xlsx";
import LevelOpexService from "../service/levelOpexService";
import Autocomplete from "@mui/material/Autocomplete";

const ExpenseItemsConfiguration = () => {
  const tableRef = useRef(null);
  const [table, setTable] = useState(null);
  const [cdcListState, setCdcListState] = useState([]);
  const [accountsListState, setAccountsListState] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "default",
    title: "",
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, ids: [] });
  const [addDialog, setAddDialog] = useState({ open: false });
  const [newRecord, setNewRecord] = useState({
    saP_OU: "",
    cdc: "",
    desc_CDC: "",
    conto: "",
    desc_Conto: "",
    liv1: "",
    liv2: "",
    liv3: "",
  });
  const [filtersActive, setFiltersActive] = useState(false);
  const cdcList = useRef([]);
  const accountsList = useRef([]);

  // Utility function to safely update table data
  const safeTableUpdate = (newData) => {
    if (!table) return;

    // Temporarily disable sorting during data update
    const currentSort = table.getSorters();
    table.clearSort();

    // Clear selections
    table.deselectRow();

    // Update the data
    table
      .setData(newData)
      .then(() => {
        // Restore sorting after data is loaded
        if (currentSort && currentSort.length) {
          setTimeout(() => {
            table.setSort(currentSort);
          }, 50);
        }
      })
      .catch((err) => {
        console.error("Error updating table data:", err);
      });
  };

  const showNotification = (message, type = "default") => {
    setSnackbar({ open: true, message, type, title: "" });
  };

  const handleAddDialogOpen = () => {
    setAddDialog({ open: true });
  };

  const handleAddDialogClose = () => {
    setAddDialog({ open: false });
    setNewRecord({
      saP_OU: "",
      cdc: "",
      desc_CDC: "",
      conto: "",
      desc_Conto: "",
      liv1: "",
      liv2: "",
      liv3: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRecord((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchData = async () => {
    try {
      const data = await LevelOpexService.getExpenseItemsConfigurations();
      if (table) {
        safeTableUpdate(data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showNotification("Error loading data", "error");
    }
  };

  const handleDownloadExcel = () => {
    if (table) {
      const data = table?.getData();
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, "Configurazione Voci Spesa");
      XLSX.writeFile(wb, "Configurazione_Voci_Spesa_Data.xlsx");
    }
  };

  const handleAddNew = async () => {
    if (
      !newRecord.saP_OU ||
      !newRecord.cdc ||
      !newRecord.desc_CDC ||
      !newRecord.conto
    ) {
      showNotification("Compilare tutti i campi obbligatori", "error");
      return;
    }
    try {
      const payload = {
        saP_OU: newRecord.saP_OU,
        cdc: newRecord.cdc,
        conto: newRecord.conto,
        liv2: newRecord.liv2,
        liv3: newRecord.liv3,
      };

      await LevelOpexService.insertExpenseItemsConfiguration(payload);

      // Clear all selections to prevent state issues
      if (table) {
        table.deselectRow();
      }

      // Get fresh data
      const data = await LevelOpexService.getExpenseItemsConfigurations();
      safeTableUpdate(data);

      showNotification("Record aggiunto con successo", "success");
      handleAddDialogClose();
    } catch (error) {
      console.error("Error adding new record:", error);
      showNotification("Error adding new record", "error");
    }
  };

  const handleDeleteSelected = () => {
    const selectedRows = table?.getSelectedRows();
    if (!selectedRows || selectedRows.length === 0) {
      showNotification("Selezionare i record da eliminare", "default");
      return;
    }
    const selectedIds = selectedRows.map((row) => row.getData()?.id);
    setDeleteDialog({ open: true, ids: selectedIds });
  };

  const handleDelete = async (ids) => {
    try {
      for (const id of ids) {
        await LevelOpexService.deleteExpenseItemsConfiguration(id);
      }

      // Clear selections before updating data
      if (table) {
        table.deselectRow();
      }

      // Get fresh data
      const data = await LevelOpexService.getExpenseItemsConfigurations();
      safeTableUpdate(data);

      showNotification(
        `${ids.length} record(s) eliminati con successo`,
        "success"
      );
    } catch (error) {
      console.error("Error deleting records:", error);
      showNotification("Error deleting records", "error");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, ids: [] });
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.ids.length > 0) {
      await handleDelete(deleteDialog.ids);
      setDeleteDialog({ open: false, ids: [] });
    }
  };

  useEffect(() => {
    if (tableRef.current && table) {
      const tableElement = table.element;

      table.getColumns().forEach((column) => {
        if (column.getDefinition().headerFilter) {
          column.setHeaderFilterValue("");
        }
      });

      if (filtersActive) {
        tableElement.classList.remove("hide-filters");
      } else {
        tableElement.classList.add("hide-filters");
      }
    }
  }, [filtersActive, table]);

  useEffect(() => {
    if (tableRef.current && !table) {
      const resizeTable = () => {
        if (tableRef.current) {
          const height = tableRef.current.parentElement.clientHeight;
          tableRef.current.style.height = `${height}px`;
          if (table) {
            table.redraw(true);
          }
        }
      };

      const resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          resizeTable();
        });
      });

      if (tableRef.current) {
        resizeObserver.observe(tableRef.current.parentElement);
      }
      const columns = [
        {
          formatter: "rowSelection",
          titleFormatter: "rowSelection",
          titleFormatterParams: { rowRange: "all" },
          hozAlign: "center",
          headerSort: false,
          width: 30,
          frozen: true,
          visible: true,
          cssClass: "frozen-column",
          columnType: "selection",
          cellClick: function (_, cell) {
            const row = cell.getRow();

            if (row.isSelected()) {
              row.deselect();
            } else {
              row.select();
            }
          },
        },
        {
          title: "SAP_OU",
          field: "saP_OU",
          widthGrow: 1,
          editor: "input",
          validator: ["required"],
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
        {
          title: "CDC",
          field: "cdc",
          widthGrow: 2,
          editor: "list",
          editorParams: {
            values: [],
            elementAttributes: {
              maxlength: "10",
            },
            verticalNavigation: "hybrid",
            sort: "asc",
            emptyValue: null,
            maxWidth: true,
            placeholderLoading: "Caricamento...",
            placeholderEmpty: "Nessun risultato",
            multiselect: false,
            filterDelay: 100,
            allowEmpty: true,
            listOnEmpty: true,
            freetext: true,
          },
          validator: "required",
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
        {
          title: "Desc CDC",
          field: "desc_CDC",
          widthGrow: 2,
          editor: "input",
          validator: "required",
          editable: false,
          headerFilter: "input",
          cssClass: "frozen-column",
        },
        {
          title: "Conto",
          field: "conto",
          widthGrow: 2,
          editor: "list",
          editorParams: {
            values: [],
            elementAttributes: {
              maxlength: "10",
            },
            verticalNavigation: "hybrid",
            sort: "asc",
            emptyValue: null,
            maxWidth: true,
            placeholderLoading: "Caricamento...",
            placeholderEmpty: "Nessun risultato",
            multiselect: false,
            filterDelay: 100,
            allowEmpty: true,
            listOnEmpty: true,
            freetext: true,
          },
          validator: "required",
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
        {
          title: "Desc Conto",
          field: "desc_Conto",
          widthGrow: 2,
          editor: "input",
          validator: "required",
          editable: false,
          headerFilter: "input",
          cssClass: "frozen-column",
        },
        {
          title: "Liv 1",
          field: "liv1",
          widthGrow: 2,
          editor: "input",
          validator: "required",
          editable: false,
          headerFilter: "input",
          cssClass: "frozen-column",
        },
        {
          title: "Liv 2",
          field: "liv2",
          widthGrow: 2,
          editor: "input",
          validator: "required",
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
        {
          title: "Liv 3",
          field: "liv3",
          widthGrow: 2,
          editor: "input",
          validator: "required",
          editable: true,
          headerFilter: "input",
          cssClass: "editable-column",
        },
      ];

      const newTable = new Tabulator(tableRef.current, {
        columns: columns,
        layout: "fitDataFill",
        pagination: "local",
        paginationSize: 100,
        paginationSizeSelector: [10, 25, 50, 100],
        movableColumns: true,
        height: "100%",
        placeholder: "Nessun dato disponibile",
        // initialSort: [{ column: "cdc", dir: "asc" }],
        rowHeight: 40,
        selectable: true,
        selectableRangeMode: "click",
        selectablePersistence: false,
        responsiveLayout: false,
        autoResize: false,
        selectableRange: true,
        selectableRangeColumns: false,
        selectableRangeRows: true,
        selectableRangeClearCells: true,
        clipboard: true,
        clipboardCopyRowRange: "range",
        clipboardPasteParser: "range",
        clipboardPasteAction: "range",
        editTriggerEvent: "dblclick",
        navigationMode: "cell",
        clipboardCopyConfig: {
          rowHeaders: false,
          columnHeaders: false,
        },
        clipboardCopyStyled: false,
        headerFilterLiveFilter: true,
        headerFilterPlaceholder: "Filter...",
      });

      newTable.on("cellEdited", async (cell) => {
        try {
          const row = cell.getRow();
          const rowData = row?.getData();

          //removing useless props
          delete rowData.desc_CDC;
          delete rowData.desc_Conto;
          delete rowData.liv1;

          const updatedData =
            await LevelOpexService.updateExpenseItemsConfiguration(rowData);

          // Update data safely
          const currentData = cell.getTable().getData();
          const updatedIndex = currentData.findIndex(
            (item) => item.id === updatedData.id
          );
          if (updatedIndex !== -1) {
            currentData[updatedIndex] = updatedData;
            safeTableUpdate(currentData);
          } else {
            cell.getTable().updateData([updatedData]);
          }

          showNotification("Dati salvati con successo", "success");
        } catch (error) {
          console.error("Error saving data:", error);
          cell.restoreOldValue();
          showNotification("Error saving changes", "error");
        }
      });

      setTable(newTable);

      return () => {
        if (table) {
          try {
            // Ensure clean teardown
            table.clearData();
            table.clearSort();
            table.deselectRow();

            setTimeout(() => {
              table.destroy();
            }, 50);
          } catch (e) {
            console.error("Error during table cleanup:", e);
          }
        }
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      };
    }
  }, []);

  const fetchCDCAndAccounts = () => {
    LevelOpexService.getCDCConfigurations().then((data) => {
      cdcList.current = data;
      setCdcListState(data);
    });
    LevelOpexService.getAccountsConfiguration().then((data) => {
      accountsList.current = data;
      setAccountsListState(data);
    });
  };

  useEffect(() => {
    if (table) {
      fetchData();
      fetchCDCAndAccounts();
    }
  }, [table]);

  useEffect(() => {
    if (table && cdcListState.length > 0 && accountsListState.length > 0) {
      // Temporarily disable sorting while updating columns
      const currentSort = table.getSorters();
      // table.clearSort();

      // Using a try-catch to handle potential errors during column updates
      try {
        // Update column definitions
        const cdcColumn = table.getColumn("cdc");
        if (cdcColumn) {
          cdcColumn.updateDefinition({
            editorParams: {
              autocomplete: true,
              values: cdcListState.map((c) => c.cdc),
              displayField: "label",
              valueField: "value",
              filterFunc: function (term, label) {
                return label?.toLowerCase().includes(term?.toLowerCase());
              },
            },
          });
        }

        const contoColumn = table.getColumn("conto");
        if (contoColumn) {
          contoColumn.updateDefinition({
            editorParams: {
              autocomplete: true,
              values: accountsListState.map((a) => a.conto),
              displayField: "label",
              valueField: "value",
              filterFunc: function (term, label) {
                return label?.toLowerCase().includes(term?.toLowerCase());
              },
            },
          });
        }
      } catch (error) {
        console.error("Error updating column definitions:", error);
      }

      // Redraw with a small delay to ensure internal state is updated
    }
  }, [table, cdcListState, accountsListState]);

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <Q8Breadcrumbs
        links={[
          {
            label: "Configurazione Voci Opex",
            href: "/expense-items-configuration",
          },
        ]}
        variant="primary"
      />

      <Box sx={{ mt: 3, mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="h4" sx={{ color: "primary.main" }}>
          Configurazione Voci Opex
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Q8Button
            variant="primary"
            label="Aggiungi nuovo"
            icon={<AddIcon />}
            onClick={handleAddDialogOpen}
            size="small"
          />
          <Q8Button
            variant="secondary"
            label="Esporta Excel"
            icon={<ExportIcon />}
            onClick={handleDownloadExcel}
            size="small"
          />
          <Q8Button
            variant="tertiary"
            label={filtersActive ? "Nascondi filtri" : "Mostra filtri"}
            icon={<FilterIcon />}
            onClick={() => {
              if (table) {
                const newState = !filtersActive;
                setFiltersActive(newState);
              }
            }}
            size="small"
          />
          <Q8Button
            variant="primary"
            label="Elimina selezionati"
            icon={<DeleteIcon />}
            onClick={handleDeleteSelected}
            size="small"
          />
        </Box>
      </Box>

      <Box
        sx={{
          my: 1,
          p: 2,
          mb: 2,
          bgcolor: "#FFF8E1",
          border: "1px solid #FFE082",
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <InfoOutlined sx={{ color: "#FB8C00" }} />
        <Typography variant="body2">
          Per modificare un record, seleziona una colonna modificabile (colonne
          gialle) e fai doppio clic sulla cella per apportare una modifica.
        </Typography>
      </Box>

      <Card sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <div ref={tableRef} style={{ flex: 1 }}></div>
      </Card>

      <Dialog
        open={addDialog.open}
        onClose={handleAddDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Aggiungi nuova Voce Spesa</DialogTitle>
        <DialogContent>
          <Box
            component="form"
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 2,
              py: 2,
            }}
          >
            <TextField
              label="SAP_OU"
              name="saP_OU"
              className="inputFieldCustom"
              value={newRecord.saP_OU}
              onChange={handleInputChange}
              fullWidth
              required
              error={!newRecord.saP_OU}
              helperText={!newRecord.saP_OU ? "Campo obbligatorio" : ""}
            />
            <Autocomplete
              disablePortal
              options={cdcList.current}
              onChange={(_, value) => {
                if (value) {
                  setNewRecord({
                    ...newRecord,
                    cdc: value.cdc,
                    desc_CDC: value.desc_CDC,
                  });
                } else {
                  setNewRecord({ ...newRecord, cdc: "", desc_CDC: "" });
                }
              }}
              getOptionLabel={(option) => option.cdc}
              fullWidth
              renderInput={(params) => (
                <>
                  <TextField
                    {...params}
                    label="CDC"
                    error={!newRecord.cdc}
                    helperText={!newRecord.cdc ? "Campo obbligatorio" : ""}
                  />
                </>
              )}
            />
            <TextField
              label="Desc CDC"
              disabled
              name="desc_CDC"
              value={newRecord.desc_CDC}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <Autocomplete
              disablePortal
              options={accountsList.current}
              getOptionLabel={(option) => option.conto}
              onChange={(_, value) => {
                if (value) {
                  setNewRecord({
                    ...newRecord,
                    desc_Conto: value.desc_IT,
                    conto: value.conto,
                    liv1: value.level1Opex,
                  });
                } else {
                  setNewRecord({
                    ...newRecord,
                    desc_Conto: "",
                    conto: "",
                    liv1: "",
                  });
                }
              }}
              fullWidth
              renderInput={(params) => (
                <>
                  <TextField
                    {...params}
                    label="Conto"
                    error={!newRecord.conto}
                    helperText={!newRecord.conto ? "Campo obbligatorio" : ""}
                  />
                </>
              )}
            />

            <TextField
              label="Desc Conto"
              disabled
              name="desc_Conto"
              value={newRecord.desc_Conto}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Liv 1"
              disabled
              name="liv1"
              value={newRecord.liv1}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Liv 2"
              name="liv2"
              value={newRecord.liv2}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              label="Liv 3"
              name="liv3"
              value={newRecord.liv3}
              onChange={handleInputChange}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Q8Button
            variant="secondary"
            label="Annulla"
            onClick={handleAddDialogClose}
          />
          <Q8Button variant="primary" label="Aggiungi" onClick={handleAddNew} />
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Conferma eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Confermi di voler eliminare {deleteDialog.ids.length} record?
            Quest'azione non può essere annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Q8Button
            variant="secondary"
            label="Annulla"
            onClick={handleDeleteCancel}
          />
          <Q8Button
            variant="primary"
            label="Elimina"
            onClick={handleDeleteConfirm}
            sx={{ backgroundColor: "#cc0e00", color: "white" }}
          />
        </DialogActions>
      </Dialog>

      <Q8SnackBar
        title={snackbar.title}
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default ExpenseItemsConfiguration;
